import React, { useState, useRef, useEffect } from 'react';
import cn from 'classnames';

interface OTPInputProps {
  length?: number;
  onComplete: (otp: string) => void;
  onReset?: () => void;
  disabled?: boolean;
  className?: string;
  autoFocus?: boolean;
  resetTrigger?: number; // Prop para triggear reset desde padre
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onComplete,
  onReset,
  disabled = false,
  className = '',
  autoFocus = false,
  resetTrigger = 0
}) => {
  const [values, setValues] = useState<string[]>(new Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Effect para reset cuando cambia resetTrigger
  useEffect(() => {
    if (resetTrigger > 0) {
      handleReset();
    }
  }, [resetTrigger]);

  // Auto focus en el primer input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleReset = () => {
    const newValues = new Array(length).fill('');
    setValues(newValues);
    onReset?.();
    // Focus en el primer input después del reset
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 0);
  };

  const handleChange = (index: number, value: string) => {
    // Solo permitir números
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    // Auto focus al siguiente input
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Verificar si está completo
    if (value && index === length - 1) {
      const otpValue = newValues.join('');
      if (otpValue.length === length) {
        onComplete(otpValue);
      }
    } else if (newValues.every(val => val !== '')) {
      onComplete(newValues.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace: limpiar actual y mover al anterior
    if (e.key === 'Backspace') {
      if (values[index]) {
        const newValues = [...values];
        newValues[index] = '';
        setValues(newValues);
      } else if (index > 0) {
        const newValues = [...values];
        newValues[index - 1] = '';
        setValues(newValues);
        inputRefs.current[index - 1]?.focus();
      }
    }
    // Arrow keys para navegación
    else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
    // Enter para submit
    else if (e.key === 'Enter') {
      const otpValue = values.join('');
      if (otpValue.length === length) {
        onComplete(otpValue);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text');
    
    // Solo permitir números y longitud correcta
    if (!/^\d+$/.test(pasteData) || pasteData.length !== length) {
      return;
    }

    const newValues = pasteData.split('');
    setValues(newValues);
    
    // Focus al último input
    inputRefs.current[length - 1]?.focus();
    
    // Llamar onComplete
    onComplete(pasteData);
  };

  return (
    <div className={cn("flex gap-2 justify-center", className)}>
      {values.map((value, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className={cn(
            "w-12 h-12 text-center text-lg font-semibold border-2 rounded-lg",
            "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent",
            "transition-all duration-200",
            {
              "border-gray-300 bg-white": !disabled && !value,
              "border-orange-500 bg-orange-50": !disabled && value,
              "border-gray-200 bg-gray-100 text-gray-400": disabled,
              "hover:border-orange-400": !disabled && !value
            }
          )}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};